import { Divider, Grid, Tab, Typography } from '@material-ui/core';
import { CustomTabs } from '@ncr-retail/base-retail-components';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { convertCurrency } from 'src/common/helpers/currency-helper';
import { LOCAL_ISO_CURRENCY_CODE } from 'src/common/helpers/localized-description-helper';
import { ITenderGroup } from 'src/common/interfaces/dtos/ITenderGroup';
import { CashOfficeMenuState } from 'src/common/interfaces/states/CashOfficeMenuState';
import { AppState } from 'src/common/redux/configure-store';
import { styles } from './styles';

type Props = TenderCountTabState & TenderCountTabProps;

const TenderCountTabContainer: React.FC<Props> = ({
  tenderMenu,
  openTenderPickupView,
  leftHanded,
}): JSX.Element => {
  const classes = styles({ leftHanded });

  const [selectedTenderGroupIndex, setSelectedTenderGroupIndex] = useState<number | boolean>(false);

  useEffect((): void => {
    let index: number | boolean = false;

    if (tenderMenu.selectedTenderGroup) {
      const selectedGroupIndex = tenderMenu.sellableTenders.tenderGroups.findIndex(
        (tenderGroup): boolean => tenderGroup.name === tenderMenu.selectedTenderGroup?.name,
      );
      if (selectedGroupIndex !== -1) {
        index = selectedGroupIndex;
      }
    }

    setSelectedTenderGroupIndex(index);
  }, [tenderMenu.selectedTenderGroup, tenderMenu.sellableTenders]);

  const getTabSubtotal = (tenderGroup: ITenderGroup): string => {
    const tenderList = tenderGroup.tenderCountList || [];
    let subtotalAmount = 0;
    tenderList.forEach(element => {
      subtotalAmount += element.amount ?? 0;
    });
    const currency = tenderGroup.tenders[0].currency;
    const isoSymbol = currency ? currency.isoSymbol : LOCAL_ISO_CURRENCY_CODE;
    return convertCurrency(subtotalAmount, isoSymbol);
  };

  const TenderGroupTab = (props: {
    tenderGroup: ITenderGroup;
    selectedTenderGroup?: ITenderGroup;
    index: number;
    onClick: (index: number) => void;
  }): JSX.Element => {
    const [selected, setSelected] = React.useState<boolean>(false);

    React.useEffect((): void => {
      setSelected(
        props.selectedTenderGroup !== undefined &&
          props.tenderGroup.name === props.selectedTenderGroup.name,
      );
    }, [props.selectedTenderGroup, props.tenderGroup.name]);

    return (
      <Tab
        label={
          <Grid container style={{ width: '22vw' }}>
            <Grid item style={{ flex: 1 }}>
              <Typography
                className={`${classes.tenderGroupTabText}`}
                data-testid={`checkout-tab-subtotal-${props.tenderGroup.name}`}
              >
                {getTabSubtotal(props.tenderGroup)}
              </Typography>
            </Grid>
            <Grid item>
              <Divider className={`${classes.tabTextDiv}`} orientation='vertical' />
            </Grid>
            <Grid
              item
              zeroMinWidth
              style={{ flex: 1 }}
              container
              direction='column'
              justifyContent='center'
            >
              <Grid item className='fullWidth'>
                <Typography
                  noWrap
                  className={`${classes.tenderGroupTabNameText} ${
                    selected ? classes.tenderGroupTabNameTextSelected : null
                  }`}
                >
                  {props.tenderGroup.name}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        }
        selected={selected}
        disableRipple
        className={selected ? classes.tenderGroupTabSelected : classes.tenderGroupTab}
        data-testid={`checkout-tab-${props.tenderGroup.name}`}
        onClick={(): void => props.onClick(props.index)}
      />
    );
  };

  return (
    <Grid container>
      <Grid
        container
        direction='row'
        className={`${classes.tenderGroupContainer} `}
        data-testid='tender-count-tab-container'
      >
        <CustomTabs
          value={selectedTenderGroupIndex}
          variant='standard'
          onChange={(event: any, index: number): void => {
            openTenderPickupView(index);
          }}
          className={classes.tabs}
          TabIndicatorProps={{ className: classes.tabsIndicator }}
          scrollButtons='off'
          orientation='vertical'
        >
          {tenderMenu.sellableTenders.tenderGroups
            .filter(tenderGroup => tenderGroup.name !== undefined)
            .map(
              (tenderGroup, index): JSX.Element => (
                <TenderGroupTab
                  tenderGroup={tenderGroup}
                  selectedTenderGroup={tenderMenu.selectedTenderGroup}
                  index={index}
                  key={index}
                  onClick={openTenderPickupView}
                />
              ),
            )}
        </CustomTabs>
      </Grid>
    </Grid>
  );
};

interface TenderCountTabState {
  tenderMenu: CashOfficeMenuState;
  leftHanded?: boolean;
}

interface TenderCountTabProps {
  openTenderPickupView: (index: number) => void;
}

const mapStateToProps = (state: AppState): TenderCountTabState => ({
  tenderMenu: state.cashOffice,
  leftHanded: state.cashierPreferences.leftHanded,
});

export default connect(mapStateToProps)(TenderCountTabContainer);
